import * as React from 'react';
import { ListItem, SingleSelectDropdown, List, DropdownButtonType, Tooltip, TextLinkWidget, TextLinkKind } from '@tableau/widgets';
import { useFormikContext, FieldArray, FieldArrayRenderProps } from 'formik';
import { Dictionary } from 'underscore';
import { RemoteAgentPresenceIcon } from '~src/app/content/RemoteAgentPresenceIcon';
import { IRemoteQueryAgent } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { IRemoteAgentConnectionStatus } from '~src/ts/ServerApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { Messages } from '~src/codegen/Localize';
import { SiteBridgeSettingsForSiteAdminProps } from '../../bridge-settings/BridgeSiteSettings';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { L10n } from '~src/ts/L10n/L10n';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';

export const REMOTE_AGENTS_FIELD = 'settings.siteAdminBridgeSettings.remoteAgentSettings.agents';
export const CONNECTION_STATUSES_FIELD = 'settings.connectionStatuses';

export function BridgeRemoteAgentsSectionBody(props: SiteBridgeSettingsForSiteAdminProps) {
  const formik = useFormikContext();
  const remoteAgents = (formik.values as SiteBridgeSettingsForSiteAdminProps).settings.siteAdminBridgeSettings.remoteAgentSettings.agents;
  const connectionStatuses: IRemoteAgentConnectionStatus[] = props.connectionStatuses;

  if (remoteAgents.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={9}>
            <div className={'tb-data-grid-cell-text tb-data-grid-bridge-center'}>
              {Messages.siteRemoteAgentNotFoundWarning_simple()}
            </div>
          </td>
        </tr>
      </tbody>);
  }

  return (
    <>
      <tbody>
        <FieldArray
          name={REMOTE_AGENTS_FIELD}
          render={arrayHelpers => (
            remoteAgents.map((agent, index) => (
              <tr key={index}>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-name-cell'}>
                    {agent.agentName}
                  </span>
                </td>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-owner-name-cell'}>
                    {agent.ownerFriendlyName}
                  </span>
                </td>
                <td>
                  {renderPooledDropDown(agent, index, arrayHelpers)}
                </td>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-version-cell'}>
                    {renderUpdateBridgeClientToolTip(agent)}
                    {agent.version}
                  </span>
                </td>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-connection-status-cell'}>
                    {renderRemoteAgentPresenceIcon(agent, connectionStatuses)}
                  </span>
                </td>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-last-used-cell'}>
                    {agent.lastUsed}
                  </span>
                </td>
                <td>
                  <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'agent-delete-cell'}>
                    {renderDeleteRemoteAgentLink(agent.agentName, index, arrayHelpers)}
                  </span>
                </td>
              </tr>))
          )}
        />
      </tbody>
    </>);
}

function renderPooledDropDown(agent: IRemoteQueryAgent, index: number, arrayHelpers: FieldArrayRenderProps) {
  const pooledListItem: ListItem = { value: Messages.siteRemoteAgentPoolNameDefault() };
  const notPooledListItem: ListItem = { value: Messages.siteRemoteAgentPoolNameNotAvailable() };
  const myItems: ListItem[] = [pooledListItem, notPooledListItem];

  return (
    <SingleSelectDropdown
      list={<List items={myItems} />}
      type={DropdownButtonType.Outline}
      value={agent.pooled ? pooledListItem.value : notPooledListItem.value}
      onChange={(value: string) => {
        arrayHelpers.replace(index, { ...agent, pooled: value === pooledListItem.value });
      }}
      label=''
      testId={'agent-pool-dropdown'}
    />
  );
}

function renderUpdateBridgeClientToolTip(agent: IRemoteQueryAgent) {
  if (agent.needsUpgrade) {
    return <Tooltip getContent={() => {
      return <span>{Messages.siteRemoteAgentVersionColumnNeedsUpgrade()}</span>;
    }}>
      <span className={'tb-remote-agent-upgrade-warning-icon'} />
    </Tooltip>;
  }
}

function renderRemoteAgentPresenceIcon(agent: IRemoteQueryAgent, remoteAgentConnectionStatuses: IRemoteAgentConnectionStatus[]) {
  if (remoteAgentConnectionStatuses.length !== 0) {
    const status = getAgentStatus(agent, remoteAgentConnectionStatuses);
    if (status !== null) {
      const currentStateStringMap: Dictionary<string> = {};
      currentStateStringMap['CONNECTED'] = Messages.remoteAgentConnectionStatus_connected_short();
      currentStateStringMap['DISCONNECTED'] = Messages.remoteAgentConnectionStatus_disconnected_short();
      currentStateStringMap['NEEDS_UPGRADE'] = Messages.remoteAgentConnectionStatus_needsUpgrade_short();
      currentStateStringMap['NOT_IN_SERVICE_MODE'] = Messages.remoteAgentConnectionStatus_notInServiceMode_short();
      currentStateStringMap['NOT_RESPONSIVE'] = Messages.remoteAgentConnectionStatus_notResponding_short();
      currentStateStringMap['THROTTLED'] = Messages.remoteAgentConnectionStatus_throttled_short();


      const remoteAgentPresenceStrings: RemoteAgentPresenceIcon.StateTextStrings = {
        stateTextConnected: Messages.remoteAgentConnectionStatus_connected(),
        stateTextNotConnected: Messages.remoteAgentConnectionStatus_disconnected(),
        stateTextNotInServiceMode: Messages.remoteAgentConnectionStatus_notInServiceMode(),
        stateTextNotResponsive: Messages.remoteAgentConnectionStatus_notResponding(),
        stateTextNeedsUpgrade: Messages.remoteAgentConnectionStatus_needsUpgrade(),
        stateTextThrottled: Messages.remoteAgentConnectionStatus_throttled()
      };

      const props: RemoteAgentPresenceIcon.Props = {
        currentState: status.connectionStatus,
        currentStateString: currentStateStringMap[status.connectionStatus],
        stateTextStrings: remoteAgentPresenceStrings,
        angularContext: null
      };

      return (<>
        <RemoteAgentPresenceIcon {...props} />
        {props.currentStateString}
      </>);
    }
  }

  return null;
}

function getAgentStatus(agent: IRemoteQueryAgent, remoteAgentConnectionStatuses: IRemoteAgentConnectionStatus[]): IRemoteAgentConnectionStatus {
  for (let i = 0; i < remoteAgentConnectionStatuses.length; i++) {
    if (remoteAgentConnectionStatuses[i].agentName === agent.agentName) {
      return remoteAgentConnectionStatuses[i];
    }
  }
  return null;
}

function renderDeleteRemoteAgentLink(agentName: string, index: number, arrayHelpers: FieldArrayRenderProps) {
  return (<>
    <TextLinkWidget
      testId={'delete-remote-agent-link'}
      onClick={() => openDeleteRemoteAgentDialog(agentName, index, arrayHelpers)}
      kind={TextLinkKind.Standalone}
    >
      {Messages.deleteMessage()}
    </TextLinkWidget>
  </>);
}

function openDeleteRemoteAgentDialog(agentName: string, index: number, arrayHelpers: FieldArrayRenderProps) {
  const dialogInstance = openReactConfirmationDialog({
    title: Messages.deleteMessage(),
    message: Messages.confirmDeleteRemoteAgent({ NAME: L10n.elideIfLong(agentName) }),
    paramsForAction: { index: index, arrayHelpers: arrayHelpers },
    actionName: Messages.deleteMessage(),
    action: ({ index, arrayHelpers }) => {
      arrayHelpers.remove(index);
      ContentActionNotification.displaySuccessToast(Messages.deleteConnectedAgentSuccess());
      return Promise.resolve();
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return Promise.resolve(dialogInstance.result);
}